import { useState, useEffect, useRef } from "react";
import { T } from "../../data/Tokens";
import { apiFetch } from "../../../api/api";

// ─── Icons ────────────────────────────────────────────────────────────────────
const SearchIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>;
const BellIcon    = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
const ChevDIcon   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4"><polyline points="6 9 12 15 18 9"/></svg>;
const MoonIcon    = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>;
const SunIcon     = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>;
const UserIcon    = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-3.5 h-3.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const SettingsIcon= () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-3.5 h-3.5"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>;
const BarIcon2    = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-3.5 h-3.5"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>;
const LogoutIcon2 = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-3.5 h-3.5"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>;
const XIcon       = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;

// ─── LocalStorage helpers for "read" tracking ─────────────────────────────────
// There's no per-admin read-state table in the DB, so we track which
// notification IDs have already been seen/read in localStorage. This survives
// refreshes but is per-browser (fine for a single-admin panel).
const READ_KEY = "admin_read_notification_ids";

function loadReadIds() {
  try {
    const raw = localStorage.getItem(READ_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}
function saveReadIds(set) {
  try {
    localStorage.setItem(READ_KEY, JSON.stringify([...set]));
  } catch {}
}

// Best-effort "time ago" formatter. Falls back to the raw string if the
// date can't be parsed (MySQL datetime strings parse fine in all modern browsers).
function timeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr.replace(" ", "T"));
  if (isNaN(d.getTime())) return dateStr;
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

// ─── Notification Dropdown ────────────────────────────────────────────────────
function NotificationDropdown({ notifications, onClose, onMarkAllRead, onItemClick, showAll, onToggleShowAll }) {
  const visible = showAll ? notifications : notifications.slice(0, 5);

  return (
    <div className="absolute right-0 top-12 w-72 max-w-[88vw] rounded-2xl overflow-hidden z-50"
      style={{ background: T.card, border: `1px solid ${T.border}`, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${T.border}` }}>
        <p className="text-[13px] font-bold" style={{ color: T.textPri }}>Notifications</p>
        {notifications.length > 0 && (
          <button onClick={onMarkAllRead} className="text-[11px] font-medium" style={{ color: T.gold }}>
            Mark all read
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="px-4 py-8 text-center text-[12px]" style={{ color: T.textSec }}>
          No new notifications
        </div>
      ) : (
       <div className="max-h-80 overflow-y-auto" data-lenis-prevent>
          {visible.map((n) => (
            <div key={n.id}
              className="flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors"
              style={{ borderBottom: `1px solid ${T.bgSoft}` }}
              onClick={() => onItemClick(n)}
              onMouseEnter={e => e.currentTarget.style.background = T.bgSoft}
              onMouseLeave={e => e.currentTarget.style.background = ""}>
              <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: n.dot }} />
              <div className="min-w-0">
                <p className="text-[12px] font-medium" style={{ color: T.textPri }}>{n.t}</p>
                <p className="text-[10px] mt-0.5 truncate" style={{ color: T.textSec }}>{n.s}</p>
                <p className="text-[10px] mt-0.5" style={{ color: "#BBBBBB" }}>{n.time}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {notifications.length > 5 && (
        <div className="px-4 py-2.5 text-center" style={{ borderTop: `1px solid ${T.border}` }}>
          <button onClick={onToggleShowAll} className="text-[12px] font-medium" style={{ color: T.gold }}>
            {showAll ? "Show less" : `View all notifications (${notifications.length})`}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Profile Dropdown ─────────────────────────────────────────────────────────
function ProfileDropdown({ setActive, onClose, onLogout }) {
  const MENU = [
    { label: "My Profile",   icon: <UserIcon />,     id: null },
    { label: "Settings",     icon: <SettingsIcon />, id: "settings" },
    { label: "Activity Log", icon: <BarIcon2 />,     id: null },
    { label: "Logout",       icon: <LogoutIcon2 />,  id: null, danger: true },
  ];
  return (
    <div className="absolute right-0 top-14 w-52 max-w-[85vw] rounded-2xl overflow-hidden z-50"
      style={{ background: T.card, border: `1px solid ${T.border}`, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
      <div className="px-4 py-3" style={{ borderBottom: `1px solid ${T.border}` }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white"
            style={{ background: `linear-gradient(135deg,${T.gold},${T.goldD})` }}>A</div>
          <div>
            <p className="text-[12px] font-bold" style={{ color: T.textPri }}>Admin User</p>
            <p className="text-[10px]" style={{ color: T.textSec }}>admin@aishcraft.com</p>
          </div>
        </div>
      </div>
      {MENU.map((item, i) => (
        <button
          key={item.label}
          onClick={() => {
            if (item.label === "Logout") {
              onLogout();
            } else if (item.id) {
              setActive(item.id);
            }
            onClose();
          }}
          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[12px] text-left transition-all"
          style={{ color: item.danger ? T.danger : T.textPri, borderBottom: i < MENU.length - 1 ? `1px solid ${T.bgSoft}` : "" }}
          onMouseEnter={e => e.currentTarget.style.background = T.bgSoft}
          onMouseLeave={e => e.currentTarget.style.background = ""}>
          <span style={{ color: item.danger ? T.danger : T.textSec }}>{item.icon}</span>
          {item.label}
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TOPBAR COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function AdminTopbar({ active, setActive, onLogout }) {
  const [notif,         setNotif]         = useState(false);
  const [profile,       setProfile]       = useState(false);
  const [dark,          setDark]          = useState(false);
  const [search,        setSearch]        = useState("");
  const [mobileSearch,  setMobileSearch]  = useState(false);
  const [notifications, setNotifications] = useState([]); // unread only
  const [showAll,       setShowAll]       = useState(false);
  const readIdsRef = useRef(loadReadIds());
  const notifRef    = useRef(null);
  const profileRef  = useRef(null);

  // Backend se live orders + newsletter signups + inquiries fetch karke
  // notifications banata hai. Har source independent hai — ek fail ho to
  // baaki chalte rahenge (Promise.allSettled).
  useEffect(() => {
    const fetchNotifications = async () => {
      const [ordersRes, newsletterRes, inquiriesRes] = await Promise.allSettled([
        apiFetch("/orders/list.php"),
        // ⚠️ newsletter files live OUTSIDE the /api folder (backend/newsletter/...),
        // and the public signup popup + admin Newsletter page both hardcode this
        // same absolute URL — so we match that here instead of using apiFetch,
        // which would 404 (it always targets backend/api/...).
        fetch("https://aishcraft.com/backend/newsletter/list.php?page=1")
          .then(r => { if (!r.ok) throw new Error(`newsletter list.php returned ${r.status}`); return r.json(); }),
        apiFetch("/inquiries/list.php"),
      ]);

      // 🔍 DEBUG — check browser console to see exactly why a source might be empty.
      // Remove these once notifications are confirmed working from all 3 sources.
      if (newsletterRes.status === "rejected") {
        console.error("[notifications] newsletter/list.php failed:", newsletterRes.reason);
      } else {
        console.log("[notifications] newsletter/list.php OK:", newsletterRes.value);
      }
      if (inquiriesRes.status === "rejected") {
        console.error("[notifications] inquiries/list.php failed:", inquiriesRes.reason);
      } else {
        console.log("[notifications] inquiries/list.php OK:", inquiriesRes.value);
      }

      // 🔍 DEBUG — check browser console to see exactly why a source might be empty.
      if (newsletterRes.status === "rejected") {
        console.error("[notifications] newsletter/list.php failed:", newsletterRes.reason);
      } else {
        console.log("[notifications] newsletter/list.php OK:", newsletterRes.value);
      }
      if (inquiriesRes.status === "rejected") {
        console.error("[notifications] inquiries/list.php failed:", inquiriesRes.reason);
      } else {
        console.log("[notifications] inquiries/list.php OK:", inquiriesRes.value);
      }

      const items = [];

      if (ordersRes.status === "fulfilled") {
        const orders = Array.isArray(ordersRes.value.data) ? ordersRes.value.data : [];
        orders.slice(0, 8).forEach(o => {
          items.push({
            id:   `order-${o.dbId}`,
            t:    "New order received",
            s:    `${o.id} · ${o.amount}`,
            time: `${o.date} · ${o.time}`,
            raw:  `${o.date} ${o.time}`,
            dot:  "#6FAE5C",
            page: "orders",
          });
        });
      }

      if (newsletterRes.status === "fulfilled") {
        const subs = Array.isArray(newsletterRes.value.data) ? newsletterRes.value.data : [];
        subs.slice(0, 8).forEach(s => {
          items.push({
            id:   `newsletter-${s.id}`,
            t:    "New newsletter signup",
            s:    s.email || s.name || "New subscriber",
            time: timeAgo(s.date),
            raw:  s.date,
            dot:  "#5B7EE5",
            page: "newsletter",
          });
        });
      }

      if (inquiriesRes.status === "fulfilled") {
        const inquiries = Array.isArray(inquiriesRes.value.data) ? inquiriesRes.value.data : [];
        inquiries.slice(0, 8).forEach(inq => {
          const name = [inq.first_name, inq.last_name].filter(Boolean).join(" ") || inq.email || "New inquiry";
          items.push({
            id:   `inquiry-${inq.id}`,
            t:    "New inquiry received",
            s:    name,
            time: timeAgo(inq.created_at),
            raw:  inq.created_at,
            dot:  "#E8B04A",
            page: "contact",
          });
        });
      }

      // Sort newest first, filter out ones already marked read, cap the list
      const sorted = items
        .sort((a, b) => new Date((b.raw || "").replace(" ", "T")) - new Date((a.raw || "").replace(" ", "T")))
        .filter(n => !readIdsRef.current.has(n.id))
        .slice(0, 30);

      setNotifications(sorted);
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Har 30 seconds mein check karega
    return () => clearInterval(interval);
  }, []);

  const markAllRead = () => {
    notifications.forEach(n => readIdsRef.current.add(n.id));
    saveReadIds(readIdsRef.current);
    setNotifications([]);
    setShowAll(false);
  };

  const handleItemClick = (n) => {
    readIdsRef.current.add(n.id);
    saveReadIds(readIdsRef.current);
    setNotifications(prev => prev.filter(x => x.id !== n.id));
    if (n.page) setActive(n.page);
    setNotif(false);
  };

  const closeAll = () => { setNotif(false); setProfile(false); };

  // Bahar (dropdown ke bahar) click karne par dropdown band ho jaye.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notif && notifRef.current && !notifRef.current.contains(e.target)) {
        setNotif(false);
      }
      if (profile && profileRef.current && !profileRef.current.contains(e.target)) {
        setProfile(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [notif, profile]);

  return (
    <header className="relative h-16 flex items-center px-3 sm:px-4 md:px-6 gap-2 sm:gap-3 md:gap-4 sticky top-0 z-20"
      style={{ background: T.card, borderBottom: `1px solid ${T.border}`, boxShadow: "0 2px 16px rgba(0,0,0,0.04)" }}>

      {mobileSearch ? (
        <div className="flex md:hidden items-center gap-2 w-full">
          <div className="flex-1 flex items-center gap-2.5 px-3 sm:px-4 py-2.5 rounded-2xl"
            style={{ background: T.bgSoft, border: `1px solid ${T.border}` }}>
            <span style={{ color: T.textSec }}><SearchIcon /></span>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search orders, products..."
              className="flex-1 text-[13px] outline-none bg-transparent min-w-0"
              style={{ color: T.textPri, fontFamily: "'DM Sans',sans-serif" }}/>
          </div>
          <button onClick={() => setMobileSearch(false)}
            className="w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center"
            style={{ color: T.textSec, border: `1px solid ${T.border}` }}>
            <XIcon />
          </button>
        </div>
      ) : (
        <>
          <span
            className="md:hidden absolute left-1/3 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[25px] leading-none whitespace-nowrap pointer-events-none"
            style={{ color: T.textPri, fontFamily: "'Dancing Script', cursive" }}
          >
            Aishcraft
          </span>

          <div className="hidden md:flex items-center gap-1.5 min-w-0 flex-shrink-0">
            <span className="text-[11px]" style={{ color: T.textSec }}>Aishcraft</span>
            <span className="text-[11px]" style={{ color: T.textSec }}>/</span>
            <span
              className="font-semibold capitalize truncate text-[11px]"
              style={{ color: T.textPri, fontFamily: "'DM Sans',sans-serif" }}
            >
              {active}
            </span>
          </div>

          <div className="md:hidden w-9 flex-shrink-0" />

          <div className="hidden md:block flex-1 max-w-md">
            <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl"
              style={{ background: T.bgSoft, border: `1px solid ${T.border}` }}>
              <span style={{ color: T.textSec }}><SearchIcon /></span>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search orders, products, customers..."
                className="flex-1 text-[13px] outline-none bg-transparent"
                style={{ color: T.textPri, fontFamily: "'DM Sans',sans-serif" }}/>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-1 sm:gap-1.5 md:gap-2 flex-shrink-0">

            <button onClick={() => setMobileSearch(true)}
              className="md:hidden w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center transition-all hover:bg-amber-50 flex-shrink-0"
              style={{ color: T.textSec, border: `1px solid ${T.border}` }}>
              <SearchIcon />
            </button>

            {/* <button onClick={() => setDark(d => !d)}
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center transition-all hover:bg-amber-50 flex-shrink-0"
              style={{ color: T.textSec, border: `1px solid ${T.border}` }}>
              {dark ? <SunIcon /> : <MoonIcon />}
            </button> */}

            <div ref={notifRef} className="relative flex-shrink-0">
              <button
                onClick={() => { setNotif(o => !o); setProfile(false); }}
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center transition-all hover:bg-amber-50 relative"
                style={{ color: T.textSec, border: `1px solid ${T.border}` }}>
                <BellIcon />
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                    style={{ background: "#FFD400" }}>
                    {notifications.length > 9 ? "9+" : notifications.length}
                  </span>
                )}
              </button>
              {notif && (
                <NotificationDropdown
                  notifications={notifications}
                  onClose={closeAll}
                  onMarkAllRead={markAllRead}
                  onItemClick={handleItemClick}
                  showAll={showAll}
                  onToggleShowAll={() => setShowAll(s => !s)}
                />
              )}
            </div>

            <div ref={profileRef} className="relative flex-shrink-0">
              <button
                onClick={() => { setProfile(o => !o); setNotif(false); }}
                className="flex items-center gap-1.5 sm:gap-2 md:gap-2.5 pl-1 pr-1.5 md:px-3 py-1 md:py-2 rounded-2xl transition-all hover:bg-amber-50"
                style={{ border: `1px solid ${T.border}` }}>
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0"
                  style={{ background: `linear-gradient(135deg,${T.gold},${T.goldD})` }}>A</div>
                <div className="text-left hidden sm:block">
                  <p className="text-[12px] font-semibold leading-tight" style={{ color: T.textPri }}>Admin</p>
                  <p className="text-[10px]" style={{ color: T.textSec }}>Aishcraft</p>
                </div>
                <span className="hidden sm:inline" style={{ color: T.textSec }}><ChevDIcon /></span>
              </button>
              {profile && (
                <ProfileDropdown
                  setActive={setActive}
                  onClose={closeAll}
                  onLogout={onLogout}
                />
              )}
            </div>
          </div>
        </>
      )}
    </header>
  );
}